import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { useElasticContext } from '../../contexts/elasticContext';
import Arrow from '../icons/dropdownArrow.svg';
import { SortingHitsProps } from './models';

import './SortingHits.scss';

const SortingHits: FC<SortingHitsProps> = ({ sortByText, firstValue, secondValue }) => {
  const [active, setActive] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<string | undefined>(firstValue);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLLIElement[]>([]);
  const btnRef = useRef<HTMLButtonElement>(null);
  const items = [
    { label: firstValue, value: 'desc' },
    { label: secondValue, value: 'asc' },
  ];

  const { handleSetSortOrder } = useElasticContext();

  const onDropdown = useCallback(() => {
    if (active) {
      setActive(false);
    } else {
      setActive(true);
      optionsRef.current[0].focus();
    }
  }, [active, optionsRef]);

  const handleOptionClick = useCallback((item) => {
    handleSetSortOrder(item.value);
    setActive(false);
    setSortBy(item.label);
  }, []);

  const handleItemKeyDown = useCallback(
    (e, item) => {
      if (e.keyCode === 13) {
        e.preventDefault();
        handleOptionClick(item);
        if (btnRef.current) btnRef.current.focus();
      }
    },
    [btnRef.current]
  );

  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (active && dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setActive(false);
      }
    };

    document.addEventListener('mousedown', checkIfClickedOutside);

    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside);
    };
  }, [active]);

  const handleKeyDown = useCallback(
    (e) => {
      if (!e.shiftKey && e.keyCode === 9) {
        if (
          active &&
          dropdownRef.current &&
          optionsRef.current &&
          optionsRef.current[optionsRef.current.length - 1] === e.target
        ) {
          setActive(false);
        }
      }

      if (e.shiftKey && e.keyCode === 9) {
        if (active && dropdownRef.current && btnRef.current === e.target) {
          setActive(false);
        }
      }

      if (e.keyCode === 40) {
        e.preventDefault();
        if (active && dropdownRef.current && optionsRef.current && e.target.nextSibling) {
          e.target.nextSibling.focus();
        }
      }

      if (e.keyCode === 38) {
        e.preventDefault();
        if (active && dropdownRef.current && e.target.previousSibling) {
          e.target.previousSibling.focus();
        }
      }
    },
    [active, dropdownRef.current, optionsRef.current]
  );

  return (
    <div className="sort-by">
      <div className="sort-by__holder">
        <span className="sort-by__text breadcrumb">{sortByText}</span>
        <div
          ref={dropdownRef}
          role="presentation"
          onKeyDown={handleKeyDown}
          className={classNames('filters__refinement', {
            'filters__refinement--active': active,
          })}
        >
          <div className="filters__refinement-dropdown">
            <button
              className="filters__refinement-btn"
              aria-expanded={active}
              type="button"
              onClick={onDropdown}
              ref={btnRef}
            >
              <span className="breadcrumb">{sortBy}</span>
              <span className="filters__refinement-icon" aria-hidden="true">
                <Arrow className="filters__refinement-pic" />
              </span>
            </button>
            <ul
              role="tablist"
              tabIndex={-1}
              className={classNames('filters__refinement-list', {
                'filters__refinement-list--active': active,
              })}
              aria-label={sortByText}
            >
              {items?.map((item, i) => (
                <li
                  role="tab"
                  key={item.label}
                  className={classNames('breadcrumb filters__refinement-item', {
                    'filters__refinement-item--active': item.label === sortBy,
                  })}
                  tabIndex={active ? 0 : -1}
                  onClick={() => {
                    handleOptionClick(item);
                  }}
                  onKeyDown={(e) => {
                    handleItemKeyDown(e, item);
                  }}
                  ref={(btn: HTMLLIElement) => {
                    optionsRef.current[i] = btn;
                  }}
                >
                  {item.label}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SortingHits;
